import {PageHeader} from '@sollapay/ui/components';
import {useGoBack, useGoForward} from '@sollapay/web-core';
import {useTranslation} from 'react-i18next';

import {
  TrustAccountBadge,
  TrustAccountBreadcrumbs,
  TrustAccountMoreMenu,
  getTrustAccountMenuActions,
  useTrustAccountMenuItems
} from '@/features/trust-accounts/components';
import {useTrustAccountPermissions} from '@/features/trust-accounts/providers';
import {TrustAccountShareControl} from '@/features/trust-accounts/share';

import {TrustAccountSellerMetaInfo} from './TrustAccountSellerMetaInfo';
import {TrustAccountViewBalanceMetric} from './TrustAccountViewBalanceMetric';
import {TrustAccountViewHeaderActions} from './TrustAccountViewHeaderActions';
import {TrustAccountViewTabs} from './TrustAccountViewTabs';
import {useShowsPayoutsTab} from '../hooks';

import type {TrustAccountViewTab} from '../utils';
import type {TrustStatus} from '@sollapay/enums';
import type {TrustAccountLedgerDTO, TrustSellerDetailsDTO} from '@sollapay/types';
import type {FC} from 'react';

export type {TrustAccountViewTab};

export interface TrustAccountViewHeaderProps {
  trustAccountId: string;
  name: string;
  refId: string;
  status: TrustStatus;
  balance?: TrustAccountLedgerDTO | null;
  balanceUpdatedAt?: number;
  isBalanceLoading?: boolean;
  sellers: TrustSellerDetailsDTO[];
  purpose?: string | null;
  transactionType?: string | null;
  activeTab: TrustAccountViewTab;
  buyersCount: number;
  transactionsCount: number;
  documentsCount: number;
  beneficiariesCount: number;
  payoutsCount: number;
  paymentInstructionsCount: number;
  onTabChange: (tab: TrustAccountViewTab) => void;
  onEditAccount: () => void;
}

export const TrustAccountViewHeader: FC<TrustAccountViewHeaderProps> = ({
  trustAccountId,
  name,
  refId,
  status,
  balance,
  balanceUpdatedAt,
  isBalanceLoading,
  sellers,
  purpose,
  transactionType,
  activeTab,
  buyersCount,
  transactionsCount,
  documentsCount,
  beneficiariesCount,
  payoutsCount,
  paymentInstructionsCount,
  onTabChange,
  onEditAccount
}) => {
  const {t} = useTranslation(['trustAccounts', 'common']);
  const back = useGoBack();
  const forward = useGoForward();
  const {canUpdateAccount} = useTrustAccountPermissions();
  const showsPayoutsTab = useShowsPayoutsTab();
  // No delete handler here by design — deleting an account is a list-page action.
  const menuActions = getTrustAccountMenuActions(status);
  const onEdit = canUpdateAccount ? onEditAccount : undefined;
  const menuItems = useTrustAccountMenuItems({actions: menuActions, onEdit});

  const headerButton = (
    <TrustAccountShareControl
      trustAccountId={trustAccountId}
      accountName={name}
      accountMenuItems={menuItems}
      fallback={
        menuItems.length > 0 ? (
          <TrustAccountMoreMenu
            actions={menuActions}
            onEdit={onEdit}
            orientation="vertical"
            variant="bordered"
          />
        ) : null
      }
    />
  );

  return (
    <PageHeader
      data-slot="trust-account-view-header"
      title={name}
      back={back}
      forward={forward}
      description={
        <TrustAccountSellerMetaInfo
          refId={refId}
          sellers={sellers}
          purpose={purpose}
          transactionType={transactionType}
        />
      }
      badge={<TrustAccountBadge status={status} showSubtitle={false} />}
      actionsBesideTabs
      actions={
        <TrustAccountViewHeaderActions
          button={headerButton}
          // The overview tab shows the balance itself.
          isBalanceHidden={activeTab === 'overview'}
          balance={
            <TrustAccountViewBalanceMetric
              status={status}
              balance={balance}
              updatedAt={balanceUpdatedAt}
              isLoading={isBalanceLoading}
            />
          }
        />
      }
      breadcrumbs={
        <TrustAccountBreadcrumbs
          title={t(`trustAccounts:view.tabs.${activeTab}`)}
          name={name}
          trustAccountId={trustAccountId}
        />
      }
      tabs={
        <TrustAccountViewTabs
          buyersCount={buyersCount}
          transactionsCount={transactionsCount}
          documentsCount={documentsCount}
          beneficiariesCount={beneficiariesCount}
          payoutsCount={payoutsCount}
          paymentInstructionsCount={paymentInstructionsCount}
          value={activeTab}
          onValueChange={onTabChange}
          showsPayoutsTab={showsPayoutsTab}
        />
      }
    />
  );
};
